import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

// Context and Hooks
import { CoreContext } from "../context/global/CoreContext";
import { usePayoutTimesheets } from "../hooks/usePayoutTimesheets";

// Utils
import { getTodayInPT } from "../utils/datetime";
import { userHasRoles, handleNavigation } from "../utils/business";

// Components
import { AppIcon } from "../components/ui";
import { Alert } from "../components/ui";
import PersonalPasswordModal from "../features/home/PersonalPasswordModal";
import SMSPermissionsModal from "../features/home/SMSPermissionsModal";

// API
import { dismissPasswordPrompt } from "../services/api/core";

// Configuration
import { getAppLinksConfig, ERROR_MESSAGES } from "../config";
import { getVisibleAppCategories } from "../config/permissionsConfig";

/**
 * Authenticated home: role-filtered app tiles plus password and SMS onboarding modals.
 *
 * @returns {JSX.Element} Home page UI.
 */
/**
 * Authenticated home: app tiles, password/SMS prompts, and payout-review badge.
 *
 * @returns {JSX.Element}
 */
export default function Home() {
  const {
    baseURL,
    user: contextUser,
    appName,
    appDomain,
    refreshUser,
  } = useContext(CoreContext);
  const navigate = useNavigate();
  const today = getTodayInPT();

  const pendingPayoutSheets = usePayoutTimesheets(contextUser);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);

  /**
   * Picks a Tailwind md:grid-cols class so home tiles stay balanced.
   *
   * @param {number} appCount Number of apps in the category.
   * @returns {string} Tailwind grid column class for md+.
   */
  const getOptimalGridCols = (appCount) => {
    if (appCount <= 1) return "md:grid-cols-1";
    if (appCount <= 2) return "md:grid-cols-2";
    if (appCount <= 3) return "md:grid-cols-3";
    if (appCount <= 4) return "md:grid-cols-2";
    if (appCount <= 6) return "md:grid-cols-3";
    if (appCount <= 8) return "md:grid-cols-4";
    if (appCount <= 9) return "md:grid-cols-3";
    if (appCount <= 12) return "md:grid-cols-4";
    // For larger numbers, default to 4 columns
    return "md:grid-cols-4";
  };

  // Set page title
  useEffect(() => {
    document.title = "R&S App";
  }, []);

  // Show password modal if personal_password is false
  useEffect(() => {
    if (contextUser && contextUser.personal_password === false) {
      setShowPasswordModal(true);
    }
  }, [contextUser]);

  // Show SMS modal only after password modal is handled
  useEffect(() => {
    if (!contextUser) return;

    const shouldShowSMSModal = contextUser.sms_permission_id === 3;
    const passwordHandled = contextUser.personal_password === true;

    if (shouldShowSMSModal && passwordHandled) {
      setShowSMSModal(true);
    }
  }, [contextUser]);

  /**
   * Closes the personal-password modal without marking the prompt complete.
   *
   * @returns {void}
   */
  const handlePasswordClose = () => {
    setShowPasswordModal(false);
    // Modal will show again on next visit since personal_password is still false
  };

  /**
   * Closes the password modal after a password is set and refreshes the user.
   *
   * @returns {Promise<void>}
   */
  const handlePasswordComplete = async () => {
    setShowPasswordModal(false);
    await refreshUser(); // Refresh to get updated personal_password = true
    // SMS modal will show automatically via useEffect if needed
  };

  /**
   * Dismisses the password prompt via API (marks personal_password true) then refreshes.
   *
   * @returns {Promise<void>}
   */
  const handlePasswordDismiss = async () => {
    // Call API to set personal_password = true without changing password
    try {
      await dismissPasswordPrompt();
      setShowPasswordModal(false);
      await refreshUser(); // Refresh to get updated personal_password = true
      // SMS modal will show automatically via useEffect if needed
    } catch (error) {
      console.error("Error dismissing password prompt:", error);
    }
  };

  /**
   * Closes the SMS permissions modal after a choice and refreshes the user.
   *
   * @returns {Promise<void>}
   */
  const handleSMSComplete = async () => {
    setShowSMSModal(false);
    await refreshUser(); // Refresh to get updated permission
  };

  /**
   * Closes the SMS modal without updating permission; it will reappear next visit.
   *
   * @returns {void}
   */
  const handleSMSClose = () => {
    setShowSMSModal(false);
    // Will show again on next visit
  };

  // Get app links configuration
  const appLinks = getAppLinksConfig(baseURL, today);

  // Add notification data to manager links
  const managerLinksWithNotifications = appLinks.manager.map((link) => ({
    ...link,
    notification: link.hasNotification ? pendingPayoutSheets : undefined,
  }));

  // Get visible app categories based on user permissions
  const visibleCategories = getVisibleAppCategories(
    contextUser,
    appLinks,
    managerLinksWithNotifications
  );

  return (
    <div className="min-h-[calc(100vh-3.5rem)] font-inter caret-transparent bg-gray-100 flex flex-col items-center">
      <main className="w-full max-w-4xl flex-1 mt-4 mx-2 px-4 md:px-2">
        {userHasRoles(contextUser) ? (
          <>
            {visibleCategories.map((category) => (
              <div key={category.key}>
                <h2 className="my-8 text-2xl font-medium text-primary-green text-center md:text-left">
                <b className="text-black">{category.title}</b> Modules:
                </h2>
                <div
                  className={`grid grid-cols-1 ${getOptimalGridCols(category.apps.length)} gap-6 mb-8`}
                >
                  {category.apps.map((app, i) => (
                    <a
                      key={i}
                      href={app.href}
                      className="block text-inherit no-underline"
                      onClick={(e) => {
                        appName.current = app.name;
                        appDomain.current = app.domain;

                        // Allow browser defaults for new-tab gestures (middle-click,
                        // Ctrl/Cmd-click, Shift-click) so real href navigation works.
                        if (
                          e.metaKey ||
                          e.ctrlKey ||
                          e.shiftKey ||
                          e.altKey
                        ) {
                          return;
                        }

                        // Internal SPA routes: client-side navigate without reload.
                        if (app.href.startsWith("/app")) {
                          e.preventDefault();
                          handleNavigation(app.href, navigate);
                        }
                      }}
                    >
                      <AppIcon app={app} />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
            <div className="w-full max-w-lg">
              <Alert message={ERROR_MESSAGES.NO_ROLES} type="warning" />
            </div>
          </div>
        )}
      </main>

      <footer className="flex h-14 w-full items-center justify-between px-4 text-xs text-gray-500 sm:px-6">
        <span>R&S Construction</span>
        <span>Operations App</span>
      </footer>

      {/* Personal Password Modal */}
      <PersonalPasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordClose}
        onDismiss={handlePasswordDismiss}
        onComplete={handlePasswordComplete}
        username={contextUser?.email || ''}
      />

      {/* SMS Permissions Modal */}
      <SMSPermissionsModal
        isOpen={showSMSModal}
        onClose={handleSMSClose}
        onComplete={handleSMSComplete}
        initialPhoneNumber={contextUser?.sms_number || ''}
      />
    </div>
  );
}
